// @ts-nocheck
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, CheckCircle } from 'lucide-react'
import * as z from 'zod'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/contexts/auth-context'
import { toast } from '@/hooks/use-toast'

// Define form validation schema
const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
})

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>

export function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const { forgotPassword } = useAuth()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  })

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setIsLoading(true)

    try {
      await forgotPassword(data.email)
      setIsSubmitted(true)
      toast({
        title: 'Reset email sent',
        description: 'Check your email for password reset instructions.',
      })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Request failed',
        description: 'Failed to send reset email. Please try again.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full max-w-md">
      <Card>
        <CardHeader className="space-y-1 text-center">
          {/* Logo */}
          <div className="inline-flex items-center gap-3 mb-6 justify-center">
            <img
              src="/logo.png"
              alt="Gathio Logo"
              className="w-8 h-8"
            />
            <span className="text-xl font-bold text-foreground">gathio</span>
          </div>

          {isSubmitted ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-2xl font-bold tracking-tight mb-4">Check your email</CardTitle>
              <CardDescription className="mb-8 leading-relaxed">
                If an account exists with this email, we've sent you secure instructions to reset your password.
              </CardDescription>

              <Button asChild className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl transition-smooth">
                <Link to="/login">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to login
                </Link>
              </Button>
            </div>
          ) : (
            <>
              <CardTitle className="text-3xl font-bold tracking-tight">Forgot password?</CardTitle>
              <CardDescription>
                No worries! Enter your email and we'll send you a reset link
              </CardDescription>
            </>
          )}
        </CardHeader>

        {!isSubmitted && (
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Email address
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  disabled={isLoading}
                  className="h-14 px-4 border border-input focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground placeholder:text-muted-foreground rounded-xl bg-input transition-smooth hover:border-primary/50 shadow-clean"
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-xs text-destructive">{errors.email.message}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl transition-smooth disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Sending instructions...
                  </div>
                ) : (
                  "Send reset instructions"
                )}
              </Button>
            </form>

            {/* Back to login link */}
            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground">
                Remember your password?{' '}
                <Link
                  to="/login"
                  className="text-primary hover:text-primary/80 font-medium transition-smooth"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  )
}
